// Web exception auto-capture.
//
// Installs `window.error` and `window.unhandledrejection` listeners that
// translate uncaught exceptions and unhandled promise rejections into a
// reserved-namespace `$exception` event with a structured property set
// matching the Tier 1 wire-protocol additions.
//
// Out of scope here: React Native's `ErrorUtils.setGlobalHandler`, mobile
// native crash collection, and source-map symbolication. Those live in
// the broader Tier 5 task.

/** Cap on the serialized stack length to keep payloads sane. */
const MAX_STACK_CHARS = 10_000;

/** Cap on the serialized message length. */
const MAX_MESSAGE_CHARS = 4_000;

/** Cap on serialized non-Error rejection values. */
const MAX_VALUE_CHARS = 1_000;

/**
 * Properties emitted alongside a `$exception` event. Names mirror the
 * `$exception_*` reservation introduced in Tier 1's protocol pass.
 */
export interface ExceptionProperties {
  $exception_type: string;
  $exception_message: string;
  $exception_stack?: string;
  $exception_source?: string;
  $exception_lineno?: number;
  $exception_colno?: number;
  /** True when the exception came from `unhandledrejection`. */
  $exception_promise_rejection: boolean;
  [key: string]: unknown;
}

/**
 * Sink that the listeners deliver `$exception` events to. The web client
 * passes its own `track()` here; tests pass a spy.
 */
export type ExceptionSink = (eventName: '$exception', properties: ExceptionProperties) => void;

interface InstallOptions {
  /**
   * Hook for surfacing diagnostic logs from inside the listener. The web
   * client wires this to its `enableDebug`-gated console.warn.
   */
  onInternalError?: (message: string, error: unknown) => void;
}

/**
 * Coerce an `unhandledrejection.reason` into a `({ type, message, stack })`
 * triple. Reasons can be anything thrown — Errors, strings, plain objects,
 * or even `undefined` — so we accept all of them.
 */
function describeRejectionReason(reason: unknown): {
  type: string;
  message: string;
  stack: string | undefined;
} {
  if (reason instanceof Error) {
    return {
      type: reason.name || 'Error',
      message: truncate(reason.message ?? String(reason), MAX_MESSAGE_CHARS),
      stack: reason.stack ? truncate(reason.stack, MAX_STACK_CHARS) : undefined
    };
  }
  if (typeof reason === 'string') {
    return {
      type: 'unhandledrejection',
      message: truncate(reason, MAX_MESSAGE_CHARS),
      stack: undefined
    };
  }
  if (reason === undefined || reason === null) {
    return {
      type: 'unhandledrejection',
      message: String(reason),
      stack: undefined
    };
  }
  // Object / number / boolean / function / etc. — best-effort serialization.
  // JSON.stringify returns `undefined` (not a string) for function values
  // and similar non-JSON inputs even though TypeScript types it as string.
  // Coerce via String() in those cases so truncate() never sees undefined.
  let serialized: string | undefined;
  try {
    serialized = JSON.stringify(reason);
  } catch {
    serialized = undefined;
  }
  if (typeof serialized !== 'string') {
    serialized = String(reason);
  }
  return {
    type: 'unhandledrejection',
    message: truncate(serialized, MAX_VALUE_CHARS),
    stack: undefined
  };
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/**
 * Invoke the consumer-supplied `onInternalError` defensively. A failing
 * callback must never escape the listener and reach the host's
 * `error`/`unhandledrejection` handler — that would re-trigger the
 * auto-capture path and risk an infinite loop.
 */
function reportInternalError(options: InstallOptions, message: string, error: unknown): void {
  if (!options.onInternalError) return;
  try {
    options.onInternalError(message, error);
  } catch {
    // Swallow: the callback itself threw; propagating defeats the
    // containment guarantee documented in installExceptionAutoCapture.
  }
}

/**
 * Build properties for an `error` event. `ErrorEvent` carries the actual
 * `Error` object on `event.error` (when one exists) plus filename/lineno
 * metadata; we prefer the structured `error` when available and fall back
 * to the legacy fields for browsers that don't supply it.
 */
function buildPropertiesFromErrorEvent(event: ErrorEvent): ExceptionProperties {
  const props: ExceptionProperties = {
    $exception_type: 'Error',
    $exception_message: '',
    $exception_promise_rejection: false
  };

  const err: unknown = event.error;
  if (err instanceof Error) {
    props.$exception_type = err.name || 'Error';
    props.$exception_message = truncate(err.message ?? String(err), MAX_MESSAGE_CHARS);
    if (err.stack) props.$exception_stack = truncate(err.stack, MAX_STACK_CHARS);
  } else {
    // Fallback: some browsers populate `event.message` even when `event.error`
    // is null (e.g. cross-origin script errors that show up as
    // "Script error.").
    props.$exception_message = truncate(event.message ?? '', MAX_MESSAGE_CHARS);
  }

  if (event.filename) props.$exception_source = event.filename;
  if (typeof event.lineno === 'number' && event.lineno > 0) {
    props.$exception_lineno = event.lineno;
  }
  if (typeof event.colno === 'number' && event.colno > 0) {
    props.$exception_colno = event.colno;
  }
  return props;
}

function buildPropertiesFromRejectionEvent(event: PromiseRejectionEvent): ExceptionProperties {
  const { type, message, stack } = describeRejectionReason(event.reason);
  const props: ExceptionProperties = {
    $exception_type: type,
    $exception_message: message,
    $exception_promise_rejection: true
  };
  if (stack !== undefined) props.$exception_stack = stack;
  return props;
}

/**
 * Install the global error + unhandledrejection listeners.
 *
 * Returns an idempotent uninstall function — calling it twice is a no-op.
 * Returns a no-op uninstall in non-browser environments (no `window`).
 */
export function installExceptionAutoCapture(
  sink: ExceptionSink,
  options: InstallOptions = {}
): () => void {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return () => {};
  }

  const errorListener = (event: ErrorEvent): void => {
    try {
      sink('$exception', buildPropertiesFromErrorEvent(event));
    } catch (e) {
      reportInternalError(options, 'exception auto-capture (error) failed', e);
    }
  };

  const rejectionListener = (event: PromiseRejectionEvent): void => {
    try {
      sink('$exception', buildPropertiesFromRejectionEvent(event));
    } catch (e) {
      reportInternalError(options, 'exception auto-capture (rejection) failed', e);
    }
  };

  window.addEventListener('error', errorListener);
  window.addEventListener('unhandledrejection', rejectionListener);

  let uninstalled = false;
  return () => {
    if (uninstalled) return;
    uninstalled = true;
    window.removeEventListener('error', errorListener);
    window.removeEventListener('unhandledrejection', rejectionListener);
  };
}
